/**
 * WS-HF N1 — which CLOUD job a given design + view is already running as.
 *
 * `deviceSubmissionLatch.ts` protects a cloud job id from being submitted to Higgsfield twice.
 * That protection is worthless if a retry never reaches the same job id — and that is exactly
 * what the shipped path did: pressing **Generate** again called `enqueueGeneration` a second
 * time, got a NEW job id, found no latch under it, and billed the operator again.
 *
 * So the app has to recognise a retry. The key is what the operator can actually repeat: the
 * design bytes and the view. `runKey` hashes the bytes (SHA-256 where the platform has it,
 * FNV-1a otherwise — this is a correlation key, not a credential) and pairs them with the view.
 *
 * Kept in `localStorage`, because the cases this protects against are a reload and a crash. If
 * storage is unavailable the in-memory map still covers the same session; `storageAvailable`
 * reports the gap so the UI can say so instead of silently offering weaker protection (N7).
 *
 * A remembered run is FORGOTTEN once the operator has seen a finished image. Pressing Generate
 * after a successful run is a deliberate request for a new one, and must not be turned into a
 * replay of the old.
 */

const PREFIX = "genz.hf.run.";

/** Same window as the submission latch: long enough to cover any generation, short enough that
 *  a stale entry cannot bind a design to a job forever. */
export const RUN_TTL_MS = 24 * 60 * 60 * 1000;

interface RunRecord {
  jobId: string;
  at: number;
}

const memory = new Map<string, RunRecord>();

function store(): Storage | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

/**
 * Can this device remember a submission across a reload? (N7)
 *
 * Probed by WRITING, not by feature-detecting: Safari's private mode and a blocked-site-data
 * policy both expose a `localStorage` object whose `setItem` throws, and an app that only
 * checks for the object would promise protection it does not have.
 */
export function storageAvailable(): boolean {
  try {
    const s = store();
    if (!s) return false;
    const probe = `${PREFIX}__probe__`;
    s.setItem(probe, "1");
    s.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/** FNV-1a over the whole buffer. Not a hash function for security — a correlation key. */
function fnv1a(bytes: Uint8Array): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < bytes.length; i++) {
    h1 ^= bytes[i];
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    // A second, differently-seeded lane: one 32-bit hash over a multi-megabyte image collides
    // far too readily for something that decides whether to spend money.
    h2 = Math.imul(h2 ^ bytes[i], 0x85ebca6b) >>> 0;
  }
  return `${h1.toString(16)}${h2.toString(16)}${bytes.length.toString(16)}`;
}

/** The key for one design + view. SHA-256 when the platform offers it. */
export async function runKey(bytes: Uint8Array, view: string): Promise<string> {
  let digest: string;
  try {
    const subtle = globalThis.crypto?.subtle;
    if (subtle) {
      const buf = await subtle.digest(
        "SHA-256",
        bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
      );
      digest = [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
    } else {
      digest = fnv1a(bytes);
    }
  } catch {
    digest = fnv1a(bytes);
  }
  return `${digest}:${view}`;
}

/** The cloud job this design + view is already running as, if any. */
export function recallRun(key: string): string | undefined {
  let rec = memory.get(key);
  if (!rec) {
    try {
      const raw = store()?.getItem(PREFIX + key);
      if (raw) rec = JSON.parse(raw) as RunRecord;
    } catch {
      return undefined;
    }
  }
  if (!rec || typeof rec.at !== "number" || !rec.jobId) return undefined;
  if (Date.now() - rec.at > RUN_TTL_MS) {
    forgetRun(key);
    return undefined;
  }
  memory.set(key, rec);
  return rec.jobId;
}

export function rememberRun(key: string, jobId: string): void {
  const rec: RunRecord = { jobId, at: Date.now() };
  memory.set(key, rec);
  try {
    store()?.setItem(PREFIX + key, JSON.stringify(rec));
  } catch {
    /* the in-memory map still covers this session */
  }
}

export function forgetRun(key: string): void {
  memory.delete(key);
  try {
    store()?.removeItem(PREFIX + key);
  } catch {
    /* ignore */
  }
}

/** Tests only. */
export function resetRunsForTest(): void {
  memory.clear();
}
